import isElectron from 'is-electron'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  getCurrentMember,
  getCurrentTitle,
  getCurrentUser,
} from 'reduxes/slices/current/selectors'
import { getNotificationsForCurrentMember } from 'reduxes/slices/notification_sounds/selectors'
import { useSelectorDelegateStore } from 'reduxes/stores/root_store/hooks'

import favicon from '../../assets/icon.png'
import { AppAudioContext } from '../contexts/app_audio_context'
import { useCallManager } from '../contexts/call_manager_context'
import usePreviousProps from '../hooks/previous_props_hook'
import { useIsAppRoute } from '../hooks/routes/use_is_app_route'
import { AppLocationContext } from '../pages/admin/app_location_provider'
import { setDestroyed } from '../reduxes/slices/api/actions'
import {
  getActiveCallIdsOnAccount,
  getActiveCallParticipantForCurrentMember,
} from '../reduxes/slices/calls/selectors'
import { getFavicons } from '../reduxes/slices/favicons/selectors'
import {useAxios} from "../hooks/use_axios";

export default function useNotificationManager() {
  const [options, setOptions] = useState({})
  const [unacknowledgedCount, setUnacknowledgedCount] = useState(0)

  const notificationsForMember = useSelector(getNotificationsForCurrentMember)
  const dispatch = useDispatch()
  const axios = useAxios()
  const currentMember = useSelectorDelegateStore(getCurrentMember)
  const currentUser = useSelectorDelegateStore(getCurrentUser)
  const currentTitle = useSelectorDelegateStore(getCurrentTitle)
  const favicons = useSelectorDelegateStore(getFavicons)
  const activeCallIds = useSelector(getActiveCallIdsOnAccount)
  const activeCallParticipant = useSelector(
    getActiveCallParticipantForCurrentMember,
  )
  const {
    activeCall: { isOnCall },
  } = useCallManager()
  const isPersonDisconnected =
    !activeCallParticipant || activeCallParticipant.disconnected_at

  const isAppRoute = useIsAppRoute()
  const navigate = useNavigate()
  const prevActiveCallIds = usePreviousProps(activeCallIds)
  const { appLocation } = useContext(AppLocationContext)
  const { playMessageSound, playRingingSound, stopAllLoops } =
    useContext(AppAudioContext)

  useEffect(() => {
    document.title = currentUser?.badge
      ? `(${currentUser.badge}) ${currentTitle}`
      : currentTitle
  }, [currentUser?.badge, currentTitle])

  useEffect(() => {
    const updateBadges = () => {
      // Send the badge details to the electron process via bridge
      if (isElectron() && !window.Cypress) {
        if (window.api?.send) {
          window.api.send('updateBadge', currentUser?.badge)
        }
      } else {
        // Only update the app favicon if it has changed
        // As these are built resources, they will not change until assets are precompiled
        const faviconLink = document.getElementById('speakeasy__favicon')

        const newBadge =
          currentUser?.badge >= 9
            ? favicons[10]
            : favicons[currentUser?.badge || 0]

        if (faviconLink && faviconLink.href !== newBadge) {
          faviconLink.href = newBadge
        }
      }

      // Post message to Swift if available
      const updateBadge = window?.webkit?.messageHandlers?.updateBadge
      if (updateBadge) {
        updateBadge.postMessage(currentUser?.badge || 0)
      }

      // Post message to Cordova if available
      if (window.api?.setBadge) {
        window.api.setBadge(currentUser?.badge || 0)
      }
    }

    updateBadges()

    // Update badges both when the window is focused, and when it's blurred
    // This ensures that badge icons update more reliably
    let removeFocusHandler, removeBlurHandler
    const electronApi = window.api
    if (electronApi?.on) {
      removeFocusHandler = electronApi.on('browser-window-focus', updateBadges)
      removeBlurHandler = electronApi.on('browser-window-blur', updateBadges)
    } else {
      window.addEventListener('focus', updateBadges)
      window.addEventListener('blur', updateBadges)
    }
    return () => {
      if (electronApi) {
        if (removeFocusHandler) removeFocusHandler()
        if (removeBlurHandler) removeBlurHandler()
      } else {
        window.removeEventListener('focus', updateBadges)
        window.removeEventListener('blur', updateBadges)
      }
    }
  }, [currentUser?.badge, favicons])

  const shouldNotificationShow = useCallback(
    (notification) => {
      switch (notification.source_type) {
        case 'Chat':
        case 'AddContactRequest':
          return (
            notification.topic_id !== appLocation.topicId ||
            !document.hasFocus()
          )
        case 'Call':
          return !(
            notification.topic_id !== appLocation.topic_id &&
            !isPersonDisconnected
          )
        default:
          return true
      }
    },
    [appLocation.topicId, appLocation.topic_id, isPersonDisconnected],
  )

  const handleClick = useCallback(
    (notification) => {
      const { url } = notification
      if (isAppRoute(url)) {
        const { pathname } = new URL(url)
        navigate(pathname)
      }
      window.focus()
    },
    [navigate, isAppRoute],
  )

  // Notifications that appear as banners or toasts from a browser
  // See models/notifications/websocket_channel.rb
  const showBrowserNotification = useCallback(
    (notification) => {
      if (!window.Notification) return

      const message = notification.message
      const title = notification.title
      const icon = notification.icon || favicon
      setOptions({
        ...options,
        tag: notification.id,
        body: message,
        lang: 'en',
        icon: icon,
        dir: 'ltr',
        silent: isOnCall,
        data: notification,
      })
      const n = new window.Notification(title, {
        tag: notification.id,
        body: message,
        lang: 'en',
        icon: icon,
        dir: 'ltr',
        silent: isOnCall,
        data: notification,
      })
      n.onclick = () => {
        handleClick(notification)
      }
    },
    [handleClick, options],
  )

  const handlePermissions = useCallback(
    (permission, notification) => {
      if (permission === 'granted') {
        showBrowserNotification(notification)
      }
    },
    [showBrowserNotification],
  )

  const setNotificationDetails = useCallback(
    (notification) => {
      if (!window.Notification) return

      if (window.Notification.permission === 'granted') {
        showBrowserNotification(notification)
        return true
      } else if (window.Notification.permission !== 'denied') {
        Notification.requestPermission((permission) => {
          handlePermissions(permission, notification)
        })
      }
    },
    [handlePermissions, showBrowserNotification],
  )

  const showNotification = useCallback(
    (notification) => {
      dispatch(setDestroyed('websocket_notifications', notification.id))
      if (!shouldNotificationShow(notification)) {
        return
      }
      setUnacknowledgedCount((prevState) => prevState + 1)
      setNotificationTitle(notification)
      if (setNotificationDetails(notification)) {
        return // System notification has fired
      }
      if (currentMember?.sound_enabled && !isOnCall) {
        switch (notification.sound_type) {
          case 'message':
            playMessageSound()
            break
          case 'call_incoming':
            playRingingSound()
            break
        }
      }
    },
    [
      currentMember?.sound_enabled,
      dispatch,
      isOnCall,
      playMessageSound,
      playRingingSound,
      setNotificationDetails,
      shouldNotificationShow,
    ],
  )

  useEffect(() => {
    notificationsForMember.forEach((notification) => {
      showNotification(notification)
    })
  }, [notificationsForMember, showNotification])

  useEffect(() => {
    if (unacknowledgedCount > 0) {
      const resetTitle = () => {
        document.title = currentTitle
        stopAllLoops()
        setUnacknowledgedCount(0)
      }

      window.addEventListener('click', resetTitle, false)

      return () => {
        window.removeEventListener('click', resetTitle, false)
      }
    }
  }, [unacknowledgedCount, currentTitle, stopAllLoops])

  useEffect(() => {
    if (prevActiveCallIds?.length && !activeCallIds?.length) {
      stopAllLoops()
    }
  }, [activeCallIds?.length, prevActiveCallIds?.length, stopAllLoops])
}

const setNotificationTitle = (notification) => {
  const shouldTitleChange = !isEmpty(notification.title)

  if (shouldTitleChange) {
    document.title = notification.title
  }
}
